<template>
  <view class="c-countdown" v-if="timesRef && !stopRef">
    <slot :times="timesRef" />
  </view>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';

interface Props {
  endStamp: number | string;
  autoStart: boolean;
  showDay: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoStart: false,
  showDay: true,
});

const emits = defineEmits(['end']);

const { endStamp, autoStart } = toRefs(props);

// 定时器
let timer: any = null;
// 时间对象
const timesRef = ref();
// 停止标识
const stopRef = ref(false);

// 对0补位
const fillZero = (n: number) => (n < 10 ? `0${n}` : n);

const getTimes = () => {
  let ev = endStamp.value;
  if (typeof ev === 'string') {
    ev = new Date(ev).getTime();
  }
  // 时间戳差值（转换秒）
  const diffMs = ev - Date.now();
  let diff = diffMs / 1000;
  diff = diff < 0 ? 0 : diff;

  // 获取天数、时分秒毫秒
  const d = Math.floor(diff / (24 * 60 * 60));
  let h = Math.floor((diff / (60 * 60)) % 24);
  const m = fillZero(Math.floor((diff / 60) % 60));
  const s = fillZero(Math.ceil(diff % 60));

  // 如果不显示天数，将天数转换为小时
  if (!props.showDay) {
    h = h + d * 24;
    return { d: '00', h: fillZero(h), m, s };
  }

  return { d: fillZero(d), h: fillZero(h), m, s };
};

// 倒计时结束事件
const end = () => {
  stop();
  emits('end');
};

// 清除倒计时定时器事件
const clear = () => timer && clearTimeout(timer);

// 停止倒计时事件
const stop = () => {
  stopRef.value = true;
  clear();
};

// 重新开启倒计时事件
const restart = () => {
  stopRef.value = false;
  start();
};

// 倒计时开启事件
const start = () => {
  if (stopRef.value) return;

  timesRef.value = getTimes();

  clear();

  if (timesRef.value.s > 0) {
    // 根据是否显示毫秒来决定定时器间隔
    timer = setTimeout(() => start(), 1000);
  } else {
    end();
  }
};

watch(
  () => endStamp.value,
  (newVal) => {
    if (newVal) {
      stopRef.value = false;
      start();
    }
    console.log(`倒计时`, newVal);
  }
);

onMounted(() => {
  // 挂载完毕时是否自动启动倒计时
  !!autoStart.value && start();
});

onBeforeUnmount(() => {
  // 卸载前清除定时器
  clear();
});

defineExpose({
  start,
  stop,
  restart,
});
</script>

<style scoped lang="scss"></style>
